import React, { useState } from "react";
import {
  HiOutlineLightningBolt,
  HiOutlineTruck,
  HiOutlineHome,
  HiOutlineOfficeBuilding,
  HiOutlineCheckCircle,
} from "react-icons/hi";
import { Zap } from "lucide-react";

/* ─── constants ──────────────────────────────────────────────────────────── */
const WHATSAPP_NUMBER = "2349116459123";

const vehicleTypes = [
  { id: "private-car",   label: "Private Car",      icon: "🚗" },
  { id: "mini-ev",       label: "Mini EV",           icon: "🚙" },
  { id: "tricycle",      label: "Tricycle",           icon: "🛺" },
  { id: "electric-bus",  label: "Electric Bus",       icon: "🚌" },
  { id: "home-charger",  label: "Home Charger",       icon: "🔌" },
  { id: "other",         label: "Other / Not sure",   icon: "❓" },
];

const usageTypes = [
  {
    id: "personal",
    label: "Personal use",
    icon: <HiOutlineHome className="text-xl shrink-0" />,
  },
  {
    id: "commercial",
    label: "Commercial / Fleet",
    icon: <HiOutlineTruck className="text-xl shrink-0" />,
  },
  {
    id: "business",
    label: "Business premises",
    icon: <HiOutlineOfficeBuilding className="text-xl shrink-0" />,
  },
];

const budgetRanges = [
  "Under ₦1,000,000",
  "₦1,000,000 – ₦5,000,000",
  "₦5,000,000 – ₦15,000,000",
  "₦15,000,000 – ₦30,000,000",
  "Above ₦30,000,000",
  "Open to financing",
];

/* ─── helpers ────────────────────────────────────────────────────────────── */
const buildMessage = (form) =>
  encodeURIComponent(
    [
      "Hello Electromob! I'd like to request a quote.",
      "",
      `*Full Name:* ${form.name}`,
      `*Email:* ${form.email}`,
      `*Phone:* ${form.phone}`,
      `*Product Interest:* ${form.vehicle || "Not specified"}`,
      `*Intended Use:* ${form.usage || "Not specified"}`,
      `*Budget Range:* ${form.budget || "Not specified"}`,
      `*Quantity:* ${form.quantity || "1"}`,
      `*Additional Notes:* ${form.notes || "None"}`,
    ].join("\n")
  );

const validate = (form) => {
  const e = {};
  if (!form.name.trim())  e.name  = "Name is required";
  if (!form.email.trim()) e.email = "Email is required";
  else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
  if (!form.phone.trim()) e.phone = "Phone number is required";
  if (!form.vehicle)      e.vehicle = "Please select a product";
  if (!form.usage)        e.usage   = "Please select intended use";
  return e;
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const Quotep = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle: "",
    usage: "",
    budget: "",
    quantity: "1",
    notes: "",
  });
  const [errors, setErrors]     = useState({});
  const [submitted, setSubmitted] = useState(false);

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      /* scroll to first error */
      document
        .querySelector("[data-error='true']")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrors({});
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${buildMessage(form)}`,
      "_blank"
    );
    setSubmitted(true);
  };

  /* ── success screen ─────────────────────────────────────────────────── */
  if (submitted) {
    return (
      <div className="bg-white min-h-screen flex flex-col">
        <div className="bg-gradient-to-br from-[#102a05] to-[#3d7a0a] pt-36 pb-20
                        px-6 md:px-10 lg:px-16 text-white">
          <span className="text-white/70 font-semibold uppercase tracking-widest text-xs">
            Get a Quote
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mt-3">
            Quote request sent!
          </h1>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center
                        text-center px-6 py-24 gap-5">
          <div className="bg-[#6ab615]/10 text-[#4a8f0f] rounded-full p-6">
            <HiOutlineCheckCircle className="text-5xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            We've received your request
          </h2>
          <p className="text-gray-500 max-w-md leading-relaxed">
            WhatsApp has opened with your details pre-filled. Our team will
            respond within 1 business day. Feel free to send a follow-up
            message directly if you need a faster reply.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-2">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 bg-[#6ab615] hover:bg-[#5a9b12]
                         text-white font-semibold px-5 py-3 rounded-xl
                         transition-colors duration-200 text-sm"
            >
              <Zap className="w-4 h-4" />
              Open WhatsApp
            </a>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm({
                  name: "", email: "", phone: "", vehicle: "",
                  usage: "", budget: "", quantity: "1", notes: "",
                });
              }}
              className="text-sm font-semibold text-gray-500 hover:text-gray-800
                         border border-gray-200 px-5 py-3 rounded-xl
                         transition-colors duration-200"
            >
              Submit another request
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── main form ──────────────────────────────────────────────────────── */
  return (
    <div className="bg-white min-h-screen">

      {/* hero band */}
      <div className="bg-gradient-to-br from-[#102a05] to-[#3d7a0a] text-white
                      pt-36 pb-20 px-6 md:px-10 lg:px-16">
        <span className="text-white/70 font-semibold uppercase tracking-widest text-xs">
          Get a Quote
        </span>
        <h1 className="text-4xl md:text-5xl font-bold mt-3 max-w-2xl leading-tight">
          Find the right EV for your needs
        </h1>
        <p className="text-white/80 mt-4 max-w-xl leading-relaxed text-sm">
          Tell us what you're looking for and we'll put together a tailored
          quote — no obligation, no pressure.
        </p>

        {/* trust pills */}
        <div className="flex flex-wrap gap-3 mt-8">
          {["Free consultation", "Flexible financing", "Same-day response"].map(
            (t) => (
              <span
                key={t}
                className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm
                           text-white text-xs font-medium px-3 py-1.5 rounded-full"
              >
                <HiOutlineCheckCircle className="shrink-0" />
                {t}
              </span>
            )
          )}
        </div>
      </div>

      {/* body */}
      <div className="px-6 md:px-10 lg:px-16 py-16 grid gap-12 lg:grid-cols-5
                      max-w-7xl mx-auto">

        {/* ── sidebar ─────────────────────────────────────────────────── */}
        <aside className="lg:col-span-2 flex flex-col gap-6">
          <SideCard
            icon={<HiOutlineLightningBolt className="text-xl" />}
            title="Why request a quote?"
            body="Every customer has different needs. A quote lets us recommend the right vehicle, payment plan, and charging setup for you specifically."
          />
          <SideCard
            icon={<HiOutlineTruck className="text-xl" />}
            title="Fleet & bulk orders"
            body="Looking to electrify a fleet? We offer dedicated pricing, fleet management advice, and coordinated delivery for bulk purchases."
          />

          {/* direct WhatsApp card */}
          <div className="bg-[#f4faec] rounded-2xl p-6 border border-[#6ab615]/20">
            <p className="text-xs uppercase tracking-widest text-[#4a8f0f]
                          font-semibold mb-3">
              Prefer to talk directly?
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Call or WhatsApp us and a member of the team will assist
              you right away.
            </p>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#6ab615] hover:bg-[#5a9b12]
                         text-white text-sm font-semibold px-4 py-2.5 rounded-xl
                         transition-colors duration-200"
            >
              <Zap className="w-4 h-4" />
              Chat on WhatsApp
            </a>
          </div>
        </aside>

        {/* ── form ────────────────────────────────────────────────────── */}
        <div className="lg:col-span-3 bg-gray-50 rounded-2xl p-8 md:p-10
                        border border-gray-100">
          <form onSubmit={handleSubmit} className="flex flex-col gap-8" noValidate>

            {/* personal details */}
            <FormSection title="Your details">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full name" error={errors.name} required>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    data-error={!!errors.name}
                    onChange={(e) => set("name", e.target.value)}
                    className={inputCls(errors.name)}
                  />
                </Field>
                <Field label="Email address" error={errors.email} required>
                  <input
                    type="email"
                    placeholder="name@email.com"
                    value={form.email}
                    data-error={!!errors.email}
                    onChange={(e) => set("email", e.target.value)}
                    className={inputCls(errors.email)}
                  />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Field label="Phone number" error={errors.phone} required>
                  <input
                    type="tel"
                    placeholder="+234 800 000 0000"
                    value={form.phone}
                    data-error={!!errors.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className={inputCls(errors.phone)}
                  />
                </Field>
                <Field label="Quantity">
                  <input
                    type="number"
                    min="1"
                    placeholder="1"
                    value={form.quantity}
                    onChange={(e) => set("quantity", e.target.value)}
                    className={inputCls()}
                  />
                </Field>
              </div>
            </FormSection>

            {/* product interest */}
            <FormSection
              title="Product interest"
              error={errors.vehicle}
              required
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {vehicleTypes.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => set("vehicle", v.label)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl
                                border text-sm font-medium transition-all duration-200
                                ${
                                  form.vehicle === v.label
                                    ? "bg-[#6ab615]/10 border-[#6ab615] text-[#4a8f0f]"
                                    : "bg-white border-gray-200 text-gray-600 hover:border-[#6ab615]/50"
                                }`}
                  >
                    <span className="text-2xl">{v.icon}</span>
                    <span className="text-center leading-tight">{v.label}</span>
                  </button>
                ))}
              </div>
            </FormSection>

            {/* intended use */}
            <FormSection
              title="Intended use"
              error={errors.usage}
              required
            >
              <div className="flex flex-col sm:flex-row gap-3">
                {usageTypes.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => set("usage", u.label)}
                    className={`flex-1 flex items-center gap-3 p-4 rounded-xl
                                border text-sm font-medium transition-all duration-200
                                ${
                                  form.usage === u.label
                                    ? "bg-[#6ab615]/10 border-[#6ab615] text-[#4a8f0f]"
                                    : "bg-white border-gray-200 text-gray-600 hover:border-[#6ab615]/50"
                                }`}
                  >
                    {u.icon}
                    {u.label}
                  </button>
                ))}
              </div>
            </FormSection>

            {/* budget */}
            <FormSection title="Budget range (optional)">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {budgetRanges.map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() =>
                      set("budget", form.budget === b ? "" : b)
                    }
                    className={`p-3 rounded-xl border text-xs font-medium
                                text-center transition-all duration-200
                                ${
                                  form.budget === b
                                    ? "bg-[#6ab615]/10 border-[#6ab615] text-[#4a8f0f]"
                                    : "bg-white border-gray-200 text-gray-600 hover:border-[#6ab615]/50"
                                }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </FormSection>

            {/* notes */}
            <FormSection title="Additional notes (optional)">
              <textarea
                rows={4}
                placeholder="Any specific requirements, questions, or preferences..."
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                className={`${inputCls()} resize-none`}
              />
            </FormSection>

            {/* submit */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center
                            gap-4 pt-2">
              <button
                type="submit"
                className="flex items-center gap-2 bg-[#6ab615] hover:bg-[#5a9b12]
                           text-white font-semibold px-8 py-4 rounded-xl
                           transition-colors duration-200 text-sm w-full sm:w-fit
                           justify-center"
              >
                <Zap className="w-4 h-4" />
                Submit via WhatsApp
              </button>
              <p className="text-xs text-gray-400 leading-relaxed max-w-xs">
                Tapping submit will open WhatsApp with your details
                pre-filled. No data is stored on this site.
              </p>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

/* ─── sub-components ─────────────────────────────────────────────────────── */
const FormSection = ({ title, children, error, required }) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <p className="text-sm font-semibold text-gray-800">
        {title}
        {required && <span className="text-red-400 ml-1">*</span>}
      </p>
      {error && (
        <p className="text-xs text-red-500 font-medium">{error}</p>
      )}
    </div>
    {children}
  </div>
);

const Field = ({ label, children, error, required }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {children}
    {error && (
      <p className="text-xs text-red-500 mt-0.5">{error}</p>
    )}
  </div>
);

const SideCard = ({ icon, title, body }) => (
  <div className="flex gap-4 items-start">
    <div className="bg-[#6ab615]/10 text-[#4a8f0f] p-3 rounded-xl shrink-0">
      {icon}
    </div>
    <div>
      <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
      <p className="text-gray-500 text-sm mt-1 leading-relaxed">{body}</p>
    </div>
  </div>
);

const inputCls = (error) =>
  `w-full px-4 py-3 rounded-lg border text-sm bg-white
   focus:outline-none focus:ring-2 focus:ring-[#6ab615]/40
   focus:border-[#6ab615] transition-colors
   ${error ? "border-red-300 bg-red-50/30" : "border-gray-200"}`;

export default Quotep;